import { notEmpty } from "@product/scmp-sdk";
import { useAtom, useAtomValue } from "jotai";
import { useRouter } from "next/router";
import qs from "qs";
import { useCallback } from "react";
import { graphql, readInlineData } from "react-relay";

import { checkCanShowInPlusPlatform } from "scmp-app/components/article/article-render/plus/helpers";
import { plusPopupAtom } from "scmp-app/components/plus/atoms";
import { QueryParameterName } from "scmp-app/components/plus/detailed-listing-popup/navigation";
import { removeQueryString } from "scmp-app/components/plus/helpers";
import { plusNewsAgendaAtom } from "scmp-app/components/plus/news-agenda/atoms";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type {
  ArticleRecirculationWidgetName,
  ArticleRecirculationWidgetPageType,
} from "scmp-app/components/tracking/google-analytics-4/types";
import { pathname as plusPathname } from "scmp-app/pages/plus";
import { pathname as plusAgendaPathname } from "scmp-app/pages/plus/agenda";
import { pathname as plusArchivePathname } from "scmp-app/pages/plus/archive";
import type { hooksPlusArticleLinkHandlerArticle$key } from "scmp-app/queries/__generated__/hooksPlusArticleLinkHandlerArticle.graphql";

import { getValidateUrl } from "./helpers";

type Ga4TrackingParameter = {
  articleId: string;
  destinationUrl: string;
  pageType: ArticleRecirculationWidgetPageType;
  widgetName: ArticleRecirculationWidgetName;
};

type OpenPopupTrackingParameter = Partial<Pick<Ga4TrackingParameter, "pageType" | "widgetName">>;

export const usePlusArticlePopup = () => {
  const router = useRouter();
  const [plusPopupState, setPlusPopupState] = useAtom(plusPopupAtom);
  const plusNewsAgendaState = useAtomValue(plusNewsAgendaAtom);

  const ga4TrackingHandler = useCallback(
    ({ articleId, destinationUrl, pageType, widgetName }: Ga4TrackingParameter) => {
      sendGA4Tracking({
        action: "click",
        category: "recirculation",
        customized_parameters: {
          ...(plusNewsAgendaState?.agendaEvent && {
            agenda_event: plusNewsAgendaState?.agendaEvent,
          }),
          ...(plusNewsAgendaState?.agendaEventDate && {
            agenda_event_date: plusNewsAgendaState?.agendaEventDate,
          }),
          article_id: articleId,
          destination_url: destinationUrl,
          page_type: pageType,
          widget_name: widgetName,
        },
        subcategory: "widget",
      });
    },
    [plusNewsAgendaState],
  );

  const handleOpen = useCallback(
    (reference: hooksPlusArticleLinkHandlerArticle$key, tracking?: OpenPopupTrackingParameter) => {
      const content = readInlineData(
        graphql`
          fragment hooksPlusArticleLinkHandlerArticle on Article @inline {
            ...helpersCheckCanShowInPlusPlatformArticle
            id
            entityId
            urlAlias
          }
        `,
        reference,
      );

      const url = getValidateUrl(content.urlAlias, window.location.origin);
      if (!url) return;

      if (tracking?.widgetName && tracking?.pageType) {
        ga4TrackingHandler({
          articleId: content.entityId,
          destinationUrl: url.toString(),
          pageType: tracking.pageType,
          widgetName: tracking.widgetName,
        });
      }

      if (!checkCanShowInPlusPlatform(content)) {
        window.open(url.toString(), "_blank");
        return;
      }

      setPlusPopupState(current => ({
        ...current,
        fromDrawerUrl:
          typeof window !== "undefined" &&
          !notEmpty(current?.fromDrawerUrl) &&
          [plusAgendaPathname, plusArchivePathname, plusPathname].includes(
            window.location.pathname,
          ) &&
          qs.parse(window.location.search, {
            ignoreQueryPrefix: true,
          })[QueryParameterName]
            ? window.location.href
            : current?.fromDrawerUrl,
        isShowArticlePopup: true,
        originalUrl: typeof window === "undefined" ? undefined : window.location.href,
      }));

      Object.entries({
        display: "plus",
        module: tracking?.widgetName ?? router.query.module,
        pgtype: tracking?.pageType ?? router.query.pgtype,
      })
        .filter(([_, value]) => notEmpty(value))
        .forEach(([key, value]) => url.searchParams.set(key, value as string));

      void router.push(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            articleId: content.entityId,
            display: "plus",
            nodeId: content.id,
          },
        },
        url.toString(),
        {
          scroll: false,
          shallow: true,
        },
      );
    },
    [ga4TrackingHandler, router, setPlusPopupState],
  );

  const getOriginalUrl = useCallback(() => {
    if (notEmpty(plusPopupState?.fromDrawerUrl)) return plusPopupState.fromDrawerUrl;
    if (!notEmpty(plusPopupState?.originalUrl)) return;

    const url = new URL(plusPopupState.originalUrl);
    if (url.pathname !== plusPathname) return;

    url.searchParams.delete(QueryParameterName);
    return url.toString();
  }, [plusPopupState.fromDrawerUrl, plusPopupState.originalUrl]);

  const handleClose = useCallback(() => {
    void router.push(
      {
        pathname: router.pathname,
        query: removeQueryString(router.query, [
          "articleId",
          "display",
          "entityUuid",
          "nodeId",
          QueryParameterName,
        ]),
      },
      getOriginalUrl(),
      {
        scroll: false,
        shallow: true,
      },
    );
    setPlusPopupState(current => ({
      ...current,
      fromDrawerUrl: undefined,
      isShowArticlePopup: false,
      originalUrl: undefined,
    }));
  }, [getOriginalUrl, router, setPlusPopupState]);

  return {
    handleClose,
    handleOpen,
  };
};
